/**
 * EngineComponents — Internal R3F components used by createEngine.
 *
 * Kept in a separate file so createEngine.tsx stays under the line limit.
 * These are implementation details — not part of the public engine API.
 *
 * @see api/createEngine.tsx
 */

import { useEffect, useLayoutEffect, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import type { AnimationManager } from '../core/AnimationManager';
import type { MaterialManager } from '../core/MaterialManager';
import type { SunlightManager } from '../core/SunlightManager';
import type { LightSignatureManager } from '../core/LightSignatureManager';
import type { WheelManager } from '../core/WheelManager';

// ---------------------------------------------------------------------------
// DebugCubeComponent
// ---------------------------------------------------------------------------

/**
 * A hot-pink 1×1×1 m cube at y=0.5 (sitting on the ground plane).
 * Toggled imperatively via the `setVisible` callback ref.
 * Use engine.setDebugCube(true/false) from the host app.
 */
export function DebugCubeComponent({
  setVisibleRef,
}: {
  setVisibleRef: { current: (v: boolean) => void };
}) {
  const [visible, setVisible] = useState(false);
  // Sync the imperative callback ref after each render (never during render).
  // useLayoutEffect ensures the ref is updated before the host reads it.
  useLayoutEffect(() => {
    setVisibleRef.current = setVisible;
  }, [setVisible, setVisibleRef]);
  if (!visible) return null;
  return (
    <mesh position={[0, 0.5, 0]} castShadow receiveShadow>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color="hotpink" roughness={0.4} metalness={0.1} />
    </mesh>
  );
}

// ---------------------------------------------------------------------------
// AnimationManagerTicker
// ---------------------------------------------------------------------------

/**
 * Invisible R3F component that calls animationManager.tick() each frame.
 * Drives the Three.js AnimationMixer from within the render loop.
 */
export function AnimationManagerTicker({ manager }: { manager: AnimationManager }) {
  const invalidate = useThree((s) => s.invalidate);
  // Bootstrap: when an API call starts playback, fire invalidate() so the
  // first frame renders even when the loop was idle (frameloop="demand").
  useEffect(() => {
    // eslint-disable-next-line react-hooks/immutability -- intentional R3F bridge: wires AnimationManager callback to Canvas invalidate
    manager.onNeedsRender = invalidate;
    return () => {
      manager.onNeedsRender = undefined;
    };
  }, [manager, invalidate]);
  useFrame((_state, delta) => {
    manager.tick(delta);
    // Request another frame while any clip is actively playing
    if (manager.isPlaying) invalidate();
  });
  return null;
}

// ---------------------------------------------------------------------------
// MaterialManagerTicker
// ---------------------------------------------------------------------------

/**
 * Invisible R3F component that calls materialManager.tick() each frame.
 * Placed inside the Canvas so it runs in the Three.js render loop.
 */
export function MaterialManagerTicker({ manager }: { manager: MaterialManager }) {
  const invalidate = useThree((s) => s.invalidate);
  // Bootstrap: when an API call changes material/color, fire invalidate() so
  // the first frame renders even when the loop was idle (frameloop="demand").
  useEffect(() => {
    // eslint-disable-next-line react-hooks/immutability -- intentional R3F bridge: wires MaterialManager callback to Canvas invalidate
    manager.onNeedsRender = invalidate;
    return () => {
      manager.onNeedsRender = undefined;
    };
  }, [manager, invalidate]);
  useFrame(() => {
    manager.tick();
    // Request another frame while color/material lerps are active
    if (manager.activeLerpCount > 0) invalidate();
  });
  return null;
}

// ---------------------------------------------------------------------------
// SunlightManagerComponent
// ---------------------------------------------------------------------------

/**
 * Renders the SunlightManager's three lights as R3F primitives and
 * drives the day-night animation via useFrame.
 * `<primitive>` auto-attaches/detaches lights when the component mounts/unmounts.
 */
export function SunlightManagerComponent({ manager }: { manager: SunlightManager }) {
  const invalidate = useThree((s) => s.invalidate);
  // Bootstrap: when apply() changes light properties, fire invalidate() so
  // the first frame renders even when the loop was idle (frameloop="demand").
  useEffect(() => {
    // eslint-disable-next-line react-hooks/immutability -- intentional R3F bridge: wires SunlightManager callback to Canvas invalidate
    manager.onNeedsRender = invalidate;
    return () => {
      manager.onNeedsRender = undefined;
    };
  }, [manager, invalidate]);
  useFrame((_state, delta) => {
    manager.tick(delta * 1000); // delta is in seconds, tick expects ms
    // Day-night animation: request another frame while animation is running
    if (manager.isAnimating) invalidate();
  });

  return (
    <>
      <primitive object={manager.hemiLight} />
      <primitive object={manager.fillLight} />
      <primitive object={manager.keyLight} />
    </>
  );
}

// ---------------------------------------------------------------------------
// LightSignatureManagerTicker
// ---------------------------------------------------------------------------

/**
 * Invisible R3F component that drives LightSignatureManager.tick() each frame.
 * Calls `invalidate()` while a fade animation is in progress so the engine's
 * `frameloop="demand"` strategy still animates the light transition smoothly.
 */
export function LightSignatureManagerTicker({ manager }: { manager: LightSignatureManager }) {
  const invalidate = useThree((s) => s.invalidate);
  // Bootstrap: when setOn() is called, fire invalidate() so the first frame
  // of the fade renders even when the loop was otherwise idle.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/immutability -- intentional R3F bridge: wires LightSignatureManager callback to Canvas invalidate
    manager.onNeedsRender = invalidate;
    return () => {
      manager.onNeedsRender = undefined;
    };
  }, [manager, invalidate]);
  useFrame((_state, delta) => {
    manager.tick(delta);
    // Request another frame while emissive intensity lerp is active
    if (manager.isFading) invalidate();
  });
  return null;
}

// ---------------------------------------------------------------------------
// WheelManagerTicker
// ---------------------------------------------------------------------------

/**
 * Invisible R3F component that drives WheelManager.tick() each frame.
 * Calls `invalidate()` while wheel spin or steering animation is active so the
 * engine's `frameloop="demand"` strategy continuously renders wheel motion.
 */
export function WheelManagerTicker({ manager }: { manager: WheelManager }) {
  const invalidate = useThree((s) => s.invalidate);
  // Bootstrap: when setRotation/setSteering is called, fire invalidate() so
  // the first wheel frame renders even when the loop was otherwise idle.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/immutability -- intentional R3F bridge: wires WheelManager callback to Canvas invalidate
    manager.onNeedsRender = invalidate;
    return () => {
      manager.onNeedsRender = undefined;
    };
  }, [manager, invalidate]);
  useFrame((_state, delta) => {
    manager.tick(delta);
    // Request another frame while spin or steering is active
    if (manager.isActive) invalidate();
  });
  return null;
}
